import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import axios from 'axios';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import './Login.css';
import { useLoginSettings } from '../../../contexts/loginSettingsContext';

const GooglePhoneNumber = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const googleUser = location.state?.googleUser;
	const { darkModeStatus } = useDarkMode();
	const { loginSettings } = useLoginSettings();

	const [phoneNumber, setPhoneNumber] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const cleanedPhoneNumber = useMemo(() => phoneNumber.replace(/\D/g, ''), [phoneNumber]);
	const loginBgFromSettings = loginSettings?.background_image;
	const defaultLoginBg = `${process.env.PUBLIC_URL}/assets/img/abstract/Main_a.jpg`;
	const loginBg =
		loginBgFromSettings && String(loginBgFromSettings).trim()
			? loginBgFromSettings
			: defaultLoginBg;

	useEffect(() => {
		document.body.classList.add('auth-page');
		return () => document.body.classList.remove('auth-page');
	}, []);

	useEffect(() => {
		if (!googleUser?.email || !googleUser?.google_id) {
			navigate('/login');
		}
	}, [googleUser, navigate]);

	if (!googleUser?.email || !googleUser?.google_id) return null;

	const handleContinue = async () => {
		if (!cleanedPhoneNumber) {
			setErrorMessage('Phone Number is required.');
			return;
		}
		if (cleanedPhoneNumber.length < 10) {
			setErrorMessage('Phone Number must be at least 10 digits.');
			return;
		}

		setErrorMessage('');
		setIsLoading(true);

		try {
			const payload = {
				name: googleUser.name,
				email: googleUser.email,
				phone: cleanedPhoneNumber,
				google_id: googleUser.google_id,
				profile_pic: googleUser.profile_pic || '',
			};

			const apiClient = axios.create({
				baseURL: process.env.REACT_APP_API_BASE_URL || '',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			const response = await apiClient.post('/reg_with_google', payload);
			const result = response?.data;
			const responseType = String(result?.type || '').toLowerCase();

			if (responseType === 'success' || result?.success === true) {
				navigate('/dashboard');
				return;
			}

			setErrorMessage(result?.message || 'Registration failed. Please try again.');
		} catch (error) {
			setErrorMessage(
				error?.response?.data?.message || 'Could not complete Google signup. Please try again.',
			);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className='bg-img' style={{ backgroundImage: `url(${loginBg})` }}>
			<PageWrapper title='Google Signup'>
				<Page
					className='p-0 container-xxl'
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '100vh',
					}}>
					<Card
						className='shadow-3d-dark'
						style={{
							maxWidth: '400px',
							width: '100%',
						}}>
						<CardBody
							style={{
								justifyContent: 'center',
							}}>
							<div className='text-center my-3'>
								<Link
									to='/'
									className={classNames('text-decoration-none fw-bold display-2', {
										'text-dark': !darkModeStatus,
										'text-light': darkModeStatus,
									})}>
									<Logo width={200} />
								</Link>
							</div>

							<div className='text-center h2 fw-bold mt-3'>Enter Your Phone Number</div>
							<div className='text-center text-muted mt-2 mb-4'>
								Complete your Google signup by adding your phone number.
							</div>

							<div className='mb-3 p-3 rounded border bg-light'>
								<div className='fw-bold'>{googleUser.name}</div>
								<div className='text-muted'>{googleUser.email}</div>
							</div>

							<form
								className='row g-4'
								onSubmit={(event) => {
									event.preventDefault();
									handleContinue();
								}}>
								<div className='col-12'>
									<FormGroup id='google-phone' isFloating label='Phone Number'>
										<Input
											type='tel'
											autoComplete='tel'
											value={phoneNumber}
											onChange={(event) => {
												setPhoneNumber(event.target.value);
												setErrorMessage('');
											}}
										/>
									</FormGroup>
									{errorMessage ? (
										<p style={{ color: 'red', fontSize: '12px' }}>{errorMessage}</p>
									) : null}
								</div>

								<div className='col-12'>
									<Button
										type='submit'
										color='success'
										className='w-100 py-3'
										disabled={isLoading}>
										{isLoading ? 'Please wait...' : 'Continue'}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Page>
			</PageWrapper>
		</div>
	);
};

export default GooglePhoneNumber;
